import { z } from "zod";

import type { MeshFormat } from "./format";
import type { ParsedMesh } from "./types";

const GLB_MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a; // 'JSON'
const CHUNK_BIN = 0x004e4942; // 'BIN\0'

const COMPONENT_FLOAT = 5126;
const COMPONENT_UINT32 = 5125;
const COMPONENT_UINT16 = 5123;
const COMPONENT_UINT8 = 5121;

const PRIMITIVE_MODE_TRIANGLES = 4;
const TRANSFORM_EPSILON = 1e-9;

const gltfJsonSchema = z
  .object({
    meshes: z
      .array(
        z
          .object({
            primitives: z.array(
              z
                .object({
                  attributes: z.record(z.string(), z.number()),
                  indices: z.number().optional(),
                  mode: z.number().optional(),
                })
                .passthrough(),
            ),
          })
          .passthrough(),
      )
      .optional(),
    nodes: z
      .array(
        z
          .object({
            mesh: z.number().optional(),
            matrix: z.array(z.number()).optional(),
            translation: z.array(z.number()).optional(),
            rotation: z.array(z.number()).optional(),
            scale: z.array(z.number()).optional(),
          })
          .passthrough(),
      )
      .optional(),
    accessors: z
      .array(
        z
          .object({
            bufferView: z.number().optional(),
            byteOffset: z.number().optional(),
            componentType: z.number(),
            count: z.number(),
            type: z.string(),
          })
          .passthrough(),
      )
      .optional(),
    bufferViews: z
      .array(
        z
          .object({
            buffer: z.number(),
            byteOffset: z.number().optional(),
            byteLength: z.number(),
            byteStride: z.number().optional(),
          })
          .passthrough(),
      )
      .optional(),
    buffers: z.array(z.object({ byteLength: z.number(), uri: z.string().optional() }).passthrough()).optional(),
  })
  .passthrough();

type GltfJson = z.infer<typeof gltfJsonSchema>;

type GltfBufferViews = NonNullable<GltfJson["bufferViews"]>;
type GltfBuffers = NonNullable<GltfJson["buffers"]>;

/**
 * Parse a .glb or .gltf file into a flat {positions, indices} mesh.
 * Reads only the first primitive of the first mesh; additional meshes and primitives are ignored.
 * External buffer URIs (.bin file references) are not supported; use .glb or embedded data URIs.
 */
export async function parseGlb(bytes: ArrayBuffer, format: Extract<MeshFormat, "glb" | "gltf">): Promise<ParsedMesh> {
  const { json, binaryBuffer } = format === "glb" ? parseGlbChunks(bytes) : await parseGltfJson(bytes);

  rejectNonIdentityNodeTransforms(json.nodes);

  const primitive = json.meshes?.[0]?.primitives[0];
  if (!primitive) throw new Error("glTF contains no mesh primitives");

  if (primitive.mode !== undefined && primitive.mode !== PRIMITIVE_MODE_TRIANGLES) {
    throw new Error(`glTF primitive mode ${primitive.mode} is not supported; only TRIANGLES is recognised`);
  }

  const posAccessorIdx = primitive.attributes["POSITION"];
  if (posAccessorIdx === undefined) throw new Error("glTF mesh primitive has no POSITION attribute");

  const accessors = json.accessors ?? [];
  const bufferViews = json.bufferViews ?? [];
  const buffers = json.buffers ?? [];

  const posAccessor = accessors[posAccessorIdx];
  if (!posAccessor) throw new Error("glTF POSITION accessor not found");

  const positions = readFloatAccessor(posAccessor, bufferViews, buffers, binaryBuffer);

  let indices: Uint32Array;
  if (primitive.indices !== undefined) {
    const idxAccessor = accessors[primitive.indices];
    if (!idxAccessor) throw new Error("glTF indices accessor not found");
    indices = readIndexAccessor(idxAccessor, bufferViews, buffers, binaryBuffer);
  } else {
    // Un-indexed: generate sequential indices
    const vertexCount = positions.length / 3;
    indices = new Uint32Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) indices[i] = i;
  }

  return { positions, indices };
}

const IDENTITY_MATRIX: readonly number[] = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
const IDENTITY_TRANSLATION: readonly number[] = [0, 0, 0];
const IDENTITY_ROTATION: readonly number[] = [0, 0, 0, 1];
const IDENTITY_SCALE: readonly number[] = [1, 1, 1];

function rejectNonIdentityNodeTransforms(nodes: GltfJson["nodes"]): void {
  if (!nodes) return;
  for (const node of nodes) {
    if (node.matrix !== undefined && !vectorsEqual(node.matrix, IDENTITY_MATRIX)) {
      throw new Error("glTF node transforms are not supported; non-identity matrix encountered");
    }
    if (node.translation !== undefined && !vectorsEqual(node.translation, IDENTITY_TRANSLATION)) {
      throw new Error("glTF node transforms are not supported; non-zero translation encountered");
    }
    if (node.rotation !== undefined && !vectorsEqual(node.rotation, IDENTITY_ROTATION)) {
      throw new Error("glTF node transforms are not supported; non-identity rotation encountered");
    }
    if (node.scale !== undefined && !vectorsEqual(node.scale, IDENTITY_SCALE)) {
      throw new Error("glTF node transforms are not supported; non-unit scale encountered");
    }
  }
}

function vectorsEqual(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.abs(a[i] - b[i]) > TRANSFORM_EPSILON) return false;
  }
  return true;
}

function parseGlbChunks(bytes: ArrayBuffer): { json: GltfJson; binaryBuffer: ArrayBuffer } {
  const view = new DataView(bytes);
  if (view.getUint32(0, true) !== GLB_MAGIC) throw new Error("Not a valid GLB file");

  let offset = 12; // skip header (magic, version, length)
  let json: GltfJson | undefined;
  let binaryBuffer: ArrayBuffer = new ArrayBuffer(0);

  while (offset < bytes.byteLength) {
    const chunkLength = view.getUint32(offset, true);
    const chunkType = view.getUint32(offset + 4, true);
    offset += 8;

    if (offset + chunkLength > bytes.byteLength) throw new Error("GLB chunk extends beyond file boundary");

    if (chunkType === CHUNK_JSON) {
      const text = new TextDecoder().decode(new Uint8Array(bytes, offset, chunkLength));
      json = gltfJsonSchema.parse(JSON.parse(text));
    } else if (chunkType === CHUNK_BIN) {
      binaryBuffer = bytes.slice(offset, offset + chunkLength);
    }
    offset += chunkLength;
  }

  if (!json) throw new Error("GLB missing JSON chunk");
  return { json, binaryBuffer };
}

async function parseGltfJson(bytes: ArrayBuffer): Promise<{ json: GltfJson; binaryBuffer: ArrayBuffer }> {
  const text = new TextDecoder().decode(new Uint8Array(bytes));
  const json = gltfJsonSchema.parse(JSON.parse(text));

  let binaryBuffer: ArrayBuffer = new ArrayBuffer(0);
  const bufferDef = json.buffers?.[0];
  if (bufferDef?.uri) {
    if (bufferDef.uri.startsWith("data:")) {
      const base64 = bufferDef.uri.split(",")[1];
      if (!base64) throw new Error("glTF data URI has no base64 payload");
      binaryBuffer = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)).buffer;
    } else {
      throw new Error("glTF external buffer URIs are not supported — use .glb or embedded data URIs");
    }
  }

  return { json, binaryBuffer };
}

function resolveBufferView(
  bvIdx: number,
  bufferViews: GltfBufferViews,
  buffers: GltfBuffers,
  binaryBuffer: ArrayBuffer,
): { data: ArrayBuffer; byteStride: number } {
  const bv = bufferViews[bvIdx];
  if (!bv) throw new Error(`bufferView ${bvIdx} not found`);

  const bufDef = buffers[bv.buffer];
  if (!bufDef) throw new Error(`buffer ${bv.buffer} not found`);

  if (bv.buffer !== 0) {
    throw new Error(`Multi-buffer glTF not supported (bufferView ${bvIdx} references buffer ${bv.buffer})`);
  }
  const sourceBuffer = binaryBuffer;
  const byteOffset = bv.byteOffset ?? 0;
  const data = sourceBuffer.slice(byteOffset, byteOffset + bv.byteLength);
  return { data, byteStride: bv.byteStride ?? 0 };
}

function readFloatAccessor(
  accessor: NonNullable<GltfJson["accessors"]>[number],
  bufferViews: GltfBufferViews,
  buffers: GltfBuffers,
  binaryBuffer: ArrayBuffer,
): Float32Array {
  if (accessor.componentType !== COMPONENT_FLOAT) {
    throw new Error(`POSITION accessor must be FLOAT, got componentType ${accessor.componentType}`);
  }
  if (accessor.bufferView === undefined) throw new Error("POSITION accessor has no bufferView");

  const { data, byteStride } = resolveBufferView(accessor.bufferView, bufferViews, buffers, binaryBuffer);
  const accessorByteOffset = accessor.byteOffset ?? 0;
  const componentCount = 3;
  const elementSize = componentCount * 4;

  const result = new Float32Array(accessor.count * componentCount);
  const view = new DataView(data);

  const stride = byteStride || elementSize;
  for (let i = 0; i < accessor.count; i++) {
    const base = accessorByteOffset + i * stride;
    result[i * 3] = view.getFloat32(base, true);
    result[i * 3 + 1] = view.getFloat32(base + 4, true);
    result[i * 3 + 2] = view.getFloat32(base + 8, true);
  }
  return result;
}

function readIndexAccessor(
  accessor: NonNullable<GltfJson["accessors"]>[number],
  bufferViews: GltfBufferViews,
  buffers: GltfBuffers,
  binaryBuffer: ArrayBuffer,
): Uint32Array {
  if (accessor.bufferView === undefined) throw new Error("Index accessor has no bufferView");

  const { data } = resolveBufferView(accessor.bufferView, bufferViews, buffers, binaryBuffer);
  const accessorByteOffset = accessor.byteOffset ?? 0;
  const result = new Uint32Array(accessor.count);
  const view = new DataView(data);

  switch (accessor.componentType) {
    case COMPONENT_UINT32: {
      for (let i = 0; i < accessor.count; i++) {
        result[i] = view.getUint32(accessorByteOffset + i * 4, true);
      }
      break;
    }
    case COMPONENT_UINT16: {
      for (let i = 0; i < accessor.count; i++) {
        result[i] = view.getUint16(accessorByteOffset + i * 2, true);
      }
      break;
    }
    case COMPONENT_UINT8: {
      for (let i = 0; i < accessor.count; i++) {
        result[i] = view.getUint8(accessorByteOffset + i);
      }
      break;
    }
    default:
      throw new Error(`Unsupported index componentType: ${accessor.componentType}`);
  }

  return result;
}
